import { useState, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { getRouteApi } from "@tanstack/react-router";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { Calendar, Clock, ArrowRight, Loader2, Search, X } from "lucide-react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";

export const defaultBlogPostImage = "https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=800&auto=format&fit=crop&q=80";

const routeApi = getRouteApi("/_site/blog/");

const Blog = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Server-rendered by the route loader.
  const { posts: allPosts } = routeApi.useLoaderData();
  const isLoading = false;

  const categories = useMemo(() => {
    if (!allPosts) return [];
    const cats = [...new Set(allPosts.map((p) => p.category))];
    return cats.sort();
  }, [allPosts]);

  const filtered = useMemo(() => {
    if (!allPosts) return [];
    let posts = allPosts;
    if (activeCategory) posts = posts.filter((p) => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      posts = posts.filter((p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }
    return posts;
  }, [allPosts, search, activeCategory]);

  return (
    <>
      <main>
        <PageHero
          badge="Our Blog"
          title="Resources & Insights"
          subtitle="Expert roofing and exterior tips for Minnesota homeowners."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="mb-10 space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search articles…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 pr-10" />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setActiveCategory(null)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!activeCategory ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>All</button>
                  {categories.map((cat) => (
                    <button key={cat} onClick={() => setActiveCategory(activeCategory === cat ? null : cat)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCategory === cat ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{cat}</button>
                  ))}
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-secondary" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No articles found{search ? ` for "${search}"` : ""}. Try a different search or category.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
                    <article className="feature-card h-full flex flex-col hover:border-secondary transition-colors overflow-hidden p-0">
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img src={post.image_url || defaultBlogPostImage} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="mb-4"><span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary/10 text-secondary">{post.category}</span></div>
                        <h2 className="text-xl font-heading font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">{post.title}</h2>
                        <p className="text-muted-foreground mb-4 flex-1">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{format(new Date(post.created_at), 'MMMM d, yyyy')}</span>
                            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{post.read_time}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default Blog;
